import { useState, useEffect } from 'react';
import { buildApiUrl } from '@/lib/utils/env';

// Module-level lightweight auth store to synchronize state across all hook consumers
type Listener = () => void;
const authStore = {
  isAuthenticated: false as boolean,
  isLoading: true as boolean,
};
const listeners = new Set<Listener>();
let bootstrapPromise: Promise<void> | null = null;

const notify = () => {
  for (const listener of listeners) {
    try {
      listener();
    } catch (error) {
      console.error('Auth listener failed', error);
    }
  }
};

const fetchSessionStatus = async () => {
  try {
    const response = await fetch(buildApiUrl('/api/admin/session'), {
      method: 'GET',
      credentials: 'include',
      headers: { 'Accept': 'application/json' },
    });
    if (!response.ok) {
      return false;
    }
    const data = await response.json().catch(() => ({}));
    return Boolean(data?.authenticated);
  } catch {
    return false;
  }
};

async function bootstrapAuth() {
  if (!bootstrapPromise) {
    bootstrapPromise = (async () => {
      try {
        authStore.isAuthenticated = await fetchSessionStatus();
      } catch /* c8 ignore next */ {
        authStore.isAuthenticated = false;
      } finally {
        authStore.isLoading = false;
        notify();
      }
    })();
  }
  return bootstrapPromise;
}

// Kick off bootstrap at module load
void bootstrapAuth();

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(authStore.isAuthenticated);
  const [isLoading, setIsLoading] = useState<boolean>(authStore.isLoading);

  useEffect(() => {
    const onChange = () => {
      setIsAuthenticated(authStore.isAuthenticated);
      setIsLoading(authStore.isLoading);
    };
    listeners.add(onChange);
    // Ensure bootstrap ran (no-op if already done)
    void bootstrapAuth();
    return () => {
      listeners.delete(onChange);
    };
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      return false;
    }

    try {
      await bootstrapAuth();

      const response = await fetch(buildApiUrl('/api/admin/login'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        return false;
      }

      const data = await response.json().catch(() => ({}));
      const authenticated = data?.success !== false;

      if (authenticated) {
        authStore.isAuthenticated = true;
        authStore.isLoading = false;
        notify();
        return true;
      }
    } catch (error) {
      console.error('Admin login failed', error);
      return false;
    }

    return false;
  };

  const logout = async () => {
    try {
      await fetch(buildApiUrl('/api/admin/logout'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Accept': 'application/json' },
      });
    } catch (error) {
      console.warn('Admin logout request failed', error);
    } finally {
      authStore.isAuthenticated = false;
      authStore.isLoading = false;
      notify();
    }
  };

  return {
    isAuthenticated,
    isLoading,
    login,
    logout
  };
};

export const __authTestUtils = {
  reset: () => {
    authStore.isAuthenticated = false;
    authStore.isLoading = true;
    listeners.clear();
    bootstrapPromise = null;
  },
  getListeners: () => listeners,
  notify: notify,
  getAuthStore: () => authStore
};
